import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Chat24Regular,
  TargetArrow24Regular,
  SlashForward24Regular,
  Sparkle24Regular,
  ArrowUpload24Regular,
  ShareAndroid24Regular,
  PersonAccounts24Regular,
  Lightbulb24Regular,
  ArrowLeft20Regular,
  ArrowRight20Regular,
  Dismiss20Regular,
  Rocket24Regular,
  QuestionCircle24Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { BookOpen20Regular } from "@fluentui/react-icons";
import styles from "./GuidedTour.module.css";

export interface TourStep {
  /** CSS selector(s) to highlight — tries each in order until one is found in the DOM */
  target: string | string[];
  /** Preferred tooltip placement relative to the highlighted element */
  placement: "top" | "bottom" | "left" | "right";
  icon: JSX.Element;
  color: string;
  title: string;
  body: string;
  tip?: string;
  /** Help article ID — shows a "View more" link that navigates to the help article */
  helpArticleId?: string;
  /** Called when this step becomes active — use to open panels, etc. */
  onEnter?: () => void;
  /** Called when leaving this step — use to close panels, etc. */
  onLeave?: () => void;
}

const TOUR_STEPS: TourStep[] = [
  {
    target: "[data-tour='topbar']",
    placement: "bottom",
    icon: <Chat24Regular />,
    color: "#004b87",
    title: "Welcome to AURA",
    body: "This is your AI-Powered Retail Assistant. AURA retrieves cited answers from Diebold Nixdorf product documentation — covering VRP, VCO, and VE.",
    tip: "Every answer is grounded in real indexed documents, never fabricated.",
    helpArticleId: "overview",
  },
  {
    target: "[data-tour='sidebar']",
    placement: "right",
    icon: <Sparkle24Regular />,
    color: "#ff7900",
    title: "Sidebar Navigation",
    body: "Browse your pinned agents, access the Agent Store, and manage your chat history all from this sidebar. Collapse it with the toggle in the icon bar.",
    helpArticleId: "agents",
  },
  {
    target: "[data-tour='agent-store']",
    placement: "right",
    icon: <Sparkle24Regular />,
    color: "#a51f7c",
    title: "Agent Store",
    body: "Browse and switch between specialized agents like RFP Intelligence Platform. Each agent has its own context and capabilities.",
    tip: "Switching agents starts a fresh context — conversations don't carry over.",
    helpArticleId: "agents",
  },
  {
    target: "[data-tour='chat-history']",
    placement: "right",
    icon: <ShareAndroid24Regular />,
    color: "#17a7ff",
    title: "Chat History",
    body: "All your conversations are saved here. Start a new chat, export your history, or continue any previous conversation.",
    tip: "After ~20 turns, start a new conversation for best results.",
    helpArticleId: "share-export",
  },
  {
    target: "[data-tour='settings-panel']",
    placement: "left",
    icon: <TargetArrow24Regular />,
    color: "#00b2a9",
    title: "Model & Response Mode",
    body: "Choose your preferred AI model and response mode here. Simple for L1 support, Standard for daily use, or Enhanced for deep technical review.",
    tip: "Standard (GPT-5 Mini) is recommended for most users. Mode is locked per conversation.",
    helpArticleId: "response-modes",
    onEnter: () => window.dispatchEvent(new CustomEvent("aura:tour-settings", { detail: { open: true } })),
    onLeave: () => window.dispatchEvent(new CustomEvent("aura:tour-settings", { detail: { open: false } })),
  },
  {
    target: "[data-tour='input-container']",
    placement: "top",
    icon: <Chat24Regular />,
    color: "#470a68",
    title: "Ask Your Question",
    body: "Type your question here. Name the product (VRP, VCO, VE), include a version if known, and describe the outcome you need. Press Enter to send.",
    tip: "Shift+Enter adds a new line without sending.",
    helpArticleId: "prompt-tips",
  },
  {
    target: "[data-tour='attach-btn']",
    placement: "top",
    icon: <ArrowUpload24Regular />,
    color: "#a6192e",
    title: "Upload Files",
    body: "Click + to attach PDF, DOCX, images and more (up to 20 MB per file, max 15 per message). AURA extracts and analyzes the content automatically.",
    helpArticleId: "attach-files",
  },
  {
    target: "[data-tour='template-picker']",
    placement: "top",
    icon: <SlashForward24Regular />,
    color: "#ff7900",
    title: "Slash Command Templates",
    body: "Open the template picker or type / in the message box. Access PRDs, epics, user stories, EARS requirements, and more — all in one keystroke.",
    tip: "Chain templates: /vrp initiative → /vrp bulk prds → /vrp prd → /vrp stories",
    helpArticleId: "slash-commands",
  },
  {
    target: "[data-tour='user-menu']",
    placement: "bottom",
    icon: <PersonAccounts24Regular />,
    color: "#004b87",
    title: "User Menu",
    body: "Access Settings, Usage Dashboard (admin only), Help & Manual, and Logout from your profile menu.",
  },
  {
    target: ["[data-tour='help-btn']", "[data-tour='user-menu']"],
    placement: "right",
    icon: <QuestionCircle24Regular />,
    color: "#00b2a9",
    title: "You're All Set!",
    body: "Anytime you need help, click the Help icon or open Help & Manual from the user menu. Now go ahead — start chatting with AURA!",
    tip: "Bookmark the Help Center for quick reference anytime.",
  },
];

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 14;
const SCROLL_MARGIN = 80;
const TOUR_SEEN_KEY = "aura_tour_completed";

interface TooltipRect {
  top: number;
  left: number;
}

function computeTooltipPosition(
  targetRect: DOMRect,
  tooltipW: number,
  tooltipH: number,
  placement: TourStep["placement"]
): { pos: TooltipRect; finalPlacement: TourStep["placement"] } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const tryPlace = (p: TourStep["placement"]): TooltipRect | null => {
    let top = 0;
    let left = 0;
    switch (p) {
      case "bottom":
        top = targetRect.bottom + TOOLTIP_GAP;
        left = targetRect.left + targetRect.width / 2 - tooltipW / 2;
        break;
      case "top":
        top = targetRect.top - tooltipH - TOOLTIP_GAP;
        left = targetRect.left + targetRect.width / 2 - tooltipW / 2;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
        left = targetRect.right + TOOLTIP_GAP;
        break;
      case "left":
        top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
        left = targetRect.left - tooltipW - TOOLTIP_GAP;
        break;
    }
    // Clamp to viewport
    left = Math.max(12, Math.min(left, vw - tooltipW - 12));
    top = Math.max(12, Math.min(top, vh - tooltipH - 12));

    // Check if there's enough room in the preferred direction
    if (p === "bottom" && targetRect.bottom + TOOLTIP_GAP + tooltipH > vh) return null;
    if (p === "top" && targetRect.top - TOOLTIP_GAP - tooltipH < 0) return null;
    if (p === "right" && targetRect.right + TOOLTIP_GAP + tooltipW > vw) return null;
    if (p === "left" && targetRect.left - TOOLTIP_GAP - tooltipW < 0) return null;

    return { top, left };
  };

  const fallbackOrder: TourStep["placement"][] = ["bottom", "right", "top", "left"];
  const preferred = tryPlace(placement);
  if (preferred) return { pos: preferred, finalPlacement: placement };

  for (const fb of fallbackOrder) {
    if (fb === placement) continue;
    const r = tryPlace(fb);
    if (r) return { pos: r, finalPlacement: fb };
  }

  // Last resort — center in viewport
  return {
    pos: {
      top: Math.max(12, (vh - tooltipH) / 2),
      left: Math.max(12, (vw - tooltipW) / 2),
    },
    finalPlacement: placement,
  };
}

interface GuidedTourProps {
  onClose: () => void;
}

export function GuidedTour({ onClose }: GuidedTourProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ w: 360, h: 300 });

  const currentStep = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  // Measure & position the target element
  const measureTarget = useCallback(() => {
    const selectors = Array.isArray(currentStep.target) ? currentStep.target : [currentStep.target];
    let el: Element | null = null;
    for (const sel of selectors) {
      el = document.querySelector(sel);
      if (el) break;
    }
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      // Scroll into view if needed
      const top = rect.top;
      const bottom = rect.bottom;
      if (top < SCROLL_MARGIN || bottom > window.innerHeight - SCROLL_MARGIN) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Re-measure after scroll
        const scrollEl = el;
        requestAnimationFrame(() => {
          setTargetRect(scrollEl.getBoundingClientRect());
        });
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep.target]);

  // Measure tooltip after render
  useEffect(() => {
    if (tooltipRef.current) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      setTooltipSize({ w: offsetWidth, h: offsetHeight });
    }
  }, [step, targetRect]);

  // Measure on step change, resize, and scroll
  useEffect(() => {
    // Fire onEnter lifecycle callback
    currentStep.onEnter?.();
    // Delay measurement to allow panels to open/render
    const delayTimer = setTimeout(() => measureTarget(), currentStep.onEnter ? 350 : 0);
    const onResize = () => measureTarget();
    const onScroll = () => measureTarget();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearTimeout(delayTimer);
      currentStep.onLeave?.();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [measureTarget, currentStep]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowRight" || e.key === "Enter") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleNext = useCallback(() => {
    if (step < TOUR_STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handlePrev = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleClose = useCallback(() => {
    TOUR_STEPS[step].onLeave?.();
    localStorage.setItem(TOUR_SEEN_KEY, "true");
    onClose();
  }, [onClose, step]);

  // Spotlight clip-path for the overlay
  const spotlightClipPath = useMemo(() => {
    if (!targetRect) return "none";
    const x = targetRect.left - SPOTLIGHT_PADDING;
    const y = targetRect.top - SPOTLIGHT_PADDING;
    const w = targetRect.width + SPOTLIGHT_PADDING * 2;
    const h = targetRect.height + SPOTLIGHT_PADDING * 2;
    const r = 8;
    // Outer rect (full viewport) + inner rounded rect (hole)
    return `polygon(evenodd,
      0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
      ${x + r}px ${y}px,
      ${x + w - r}px ${y}px,
      ${x + w}px ${y + r}px,
      ${x + w}px ${y + h - r}px,
      ${x + w - r}px ${y + h}px,
      ${x + r}px ${y + h}px,
      ${x}px ${y + h - r}px,
      ${x}px ${y + r}px,
      ${x + r}px ${y}px
    )`;
  }, [targetRect]);

  // Box-shadow approach is more compatible than clip-path for this use case
  const spotlightStyle = useMemo(() => {
    if (!targetRect) return {};
    return {
      top: targetRect.top - SPOTLIGHT_PADDING,
      left: targetRect.left - SPOTLIGHT_PADDING,
      width: targetRect.width + SPOTLIGHT_PADDING * 2,
      height: targetRect.height + SPOTLIGHT_PADDING * 2,
    };
  }, [targetRect]);

  const tooltipPos = useMemo(() => {
    if (!targetRect) {
      return {
        pos: {
          top: window.innerHeight / 2 - tooltipSize.h / 2,
          left: window.innerWidth / 2 - tooltipSize.w / 2,
        },
        finalPlacement: currentStep.placement,
      };
    }
    return computeTooltipPosition(
      targetRect,
      tooltipSize.w,
      tooltipSize.h,
      currentStep.placement
    );
  }, [targetRect, tooltipSize, currentStep.placement]);

  return (
    <div className={styles.tourRoot}>
      {/* Overlay backdrop */}
      <div className={styles.overlay} onClick={handleClose} />

      {/* Spotlight cutout */}
      {targetRect && (
        <div
          className={styles.spotlight}
          style={spotlightStyle}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={styles.tooltip}
        style={{
          top: tooltipPos.pos.top,
          left: tooltipPos.pos.left,
        }}
        data-placement={tooltipPos.finalPlacement}
        key={step}
      >
        {/* Header */}
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipIconWrap} style={{ backgroundColor: currentStep.color + "18", color: currentStep.color }}>
            {currentStep.icon}
          </div>
          <div className={styles.tooltipMeta}>
            <span className={styles.tooltipStepLabel}>
              {step + 1} of {TOUR_STEPS.length}
            </span>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close tour">
            <Dismiss20Regular />
          </button>
        </div>

        {/* Body */}
        <h3 className={styles.tooltipTitle}>{currentStep.title}</h3>
        <p className={styles.tooltipBody}>{currentStep.body}</p>

        {currentStep.tip && (
          <div className={styles.tooltipTip}>
            <Lightbulb24Regular className={styles.tipIcon} />
            <span>{currentStep.tip}</span>
          </div>
        )}

        {currentStep.helpArticleId && (
          <button
            className={styles.viewMoreLink}
            onClick={() => {
              const articleId = currentStep.helpArticleId!;
              handleClose();
              navigate("/help", { state: { articleId } });
            }}
          >
            <BookOpen20Regular />
            <span>View more in Help Center</span>
          </button>
        )}

        {/* Footer */}
        <div className={styles.tooltipFooter}>
          <div className={styles.dots}>
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                className={`${styles.dot} ${i === step ? styles.dotActive : ""} ${i < step ? styles.dotDone : ""}`}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
          <div className={styles.navButtons}>
            {!isFirst && (
              <button className={styles.btnBack} onClick={handlePrev}>
                <ArrowLeft20Regular />
                <span>Back</span>
              </button>
            )}
            <button className={styles.btnNext} onClick={handleNext}>
              <span>{isLast ? "Finish" : "Next"}</span>
              {isLast ? <Rocket24Regular /> : <ArrowRight20Regular />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Check if user has already seen the tour */
export function hasTourBeenSeen(): boolean {
  return localStorage.getItem(TOUR_SEEN_KEY) === "true";
}

/** Reset tour to show again */
export function resetTour(): void {
  localStorage.removeItem(TOUR_SEEN_KEY);
}
